import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { LoadingStatusWrapper, PopOutChart, InfoMessage, GeneralButton } from 'sharedComponents';
import { useTranslation } from 'react-i18next';
import useChartConfig from './useChartConfig';
import { chartHeightConstants, chartModules, default3DOptions, PiezoSensor } from './constants';
import './style.scss';
import { getSpectrumRangeOptions } from './spectrumRangeOptions';
import {
    ChartViewToggleButton,
    PopOutButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup,
    XAxisToggleButton
} from 'sharedComponents/Chart/headers';
import { map, some } from 'lodash';
import FFTHistoricalTimestampDialog from './FFTHistoricalTimestampDialog';
import CustomLegend from './CustomLegend';
import { apiCallStatus } from 'helpers/constants';
import useChartResize from 'sharedHooks/useChartResize';


const VibrationFFTComponent = (props) => {

    const { t: translate } = useTranslation();
    const { model, actions, selectedTimestamps } = props;
    const spectrumRangeOptions = getSpectrumRangeOptions(translate, model.speed, model.sensorTypeID, model.fftData);
    const containerRef = useRef(null);
    const chartConfig = useChartConfig(model.fftData, model.selectedSpectrumRange, model.selectedLegends, selectedTimestamps, containerRef);
    const [showHistoricalDialog, setShowHistoricalDialog] = useState(false);
    const [chartInstance, setChartInstance] = useState(null);
    const chartInstanceRef = useRef(null);
    const chartRef = useRef(null);
    const fftData = false;
    const getValue = () => {
        return spectrumRangeOptions.find(option => option.id === model.selectedSpectrumRange);
    };
    const setZoomBySpectrumRange = (mainChartInst) => (xAxisExtreme) => {
        mainChartInst?.xAxis[0]?.setExtremes(0, xAxisExtreme);
        const xAxisZeroExtremes = mainChartInst?.xAxis[0].getExtremes();
        mainChartInst?.xAxis[1]?.setExtremes(0, xAxisZeroExtremes.max * 60);
        mainChartInst?.yAxis[0]?.setExtremes();
    };
    const zoomOut = (mainChartInst) => {
        mainChartInst.zoomOut();
        const selectionValue = getValue();
        setZoomBySpectrumRange(mainChartInst)(selectionValue ? selectionValue.xAxisZoomValue : spectrumRangeOptions[1].xAxisZoomValue);
    };

    const updateChartHeight = () => {
        if (chartInstanceRef?.current && containerRef?.current) {
            const containerHeight = containerRef?.current?.clientHeight || chartHeightConstants.default;
            const availableHeight = Math.max(chartHeightConstants.min, containerHeight - chartHeightConstants.reserved); // Minimum 300px
            const finalHeight = Math.min(availableHeight, chartHeightConstants.max); // Maximum 1000px
            chartInstanceRef.current.update({
                chart: {
                    height: finalHeight
                }
            }, true, true);
        }
    };

    useEffect(() => {
        window.addEventListener('resize', updateChartHeight);

        return () => {
            window.removeEventListener('resize', updateChartHeight);
        };
    }, []);

    useEffect(() => {
        if (!containerRef?.current) {
            return;
        }

        const updateChartWidth = () => {
            if (chartInstanceRef?.current && containerRef?.current) {
                const containerWidth = containerRef.current.clientWidth;
                if (containerWidth) {
                    chartInstanceRef.current.update({
                        chart: { width: containerWidth }
                    }, true, false);
                }
            }
        };

        const observer = new ResizeObserver(updateChartWidth);
        observer.observe(containerRef.current);

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (model?.fftDataLoadingStatus === apiCallStatus.SUCCESS && model?.fftData) {
            const isPiezoAvailable = some(model?.fftData, (fft) => fft?.dataValues?.some(data => data.sensorAxisName === PiezoSensor.axisName));
            if (!isPiezoAvailable && model?.selectedSpectrumRange === PiezoSensor.id) {
                actions.setSelectedSpectrumRange(spectrumRangeOptions[1]?.id);
            }
        }
    }, [model?.fftData]);

    useEffect(() => {
        chartInstanceRef.current = chartInstance;
        if (chartRef.current) {
            chartRef.current.chart = chartInstance;
        }
    }, [chartInstance]);

    useChartResize(containerRef, chartRef);

    const historicalTimeStamp = (props) => {
        const { mainChartInst } = props;
        return mainChartInst ? <div key='fft-historical' className='historical-timestamp'>
            <GeneralButton
                type='normal'
                text={`${translate('ABB.Powertrain.Frontend.vibrationFFTSelectTimestamp')}${selectedTimestamps.length ? ` (${selectedTimestamps.length})` : ''}`}
                onClick={() => setShowHistoricalDialog(true)}
            />
            <div className='fft-options'>
                {
                    map(spectrumRangeOptions, option =>
                        <>
                            {option.id === PiezoSensor.id && <div className='vertical-line' />}
                            <GeneralButton
                                key={option.id}
                                type='discreet'
                                className={`fft-option-btn ${option.id === model.selectedSpectrumRange ? 'selected' : ''}`}
                                text={option.title}
                                onClick={() => {
                                    setZoomBySpectrumRange(mainChartInst)(option.xAxisZoomValue);
                                    actions.setSelectedSpectrumRange(option.id);
                                }}
                            />
                        </>
                    )
                }
            </div>

        </div> : null;
    };

    const MainChartHeader = withPopOutRow([
        withLeftButtonGroup([historicalTimeStamp]),
        withRightButtonGroup([XAxisToggleButton, ChartViewToggleButton, PopOutButton])
    ]);

    const PopOutChartHeader = withPopOutRow([
        withLeftButtonGroup([]),
        withRightButtonGroup([XAxisToggleButton, PopOutButton])
    ]);

    return (
        <div ref={containerRef} style={{ height: '100%' }}>
            <LoadingStatusWrapper
                useCustomAnchor
                transparent
                indicatorConfig={{ placement: 'sticky' }}
                loadingStatus={[model?.fftDataLoadingStatus]} >
                <PopOutChart
                    hasKpi={model?.hasRawData}
                    data={fftData}
                    renderNoKpi={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.noDataLabelAvailable'
                        text='ABB.Powertrain.Frontend.collectRawDataLabel' />}
                    selectedTimestamps={selectedTimestamps}
                    loadingStatus={model?.fftDataLoadingStatus}
                    options={chartConfig}
                    modules={chartModules}
                    container='op-fft-chart-v2'
                    zoomOut={zoomOut}
                    mainChartHeader={MainChartHeader}
                    popOutChartHeader={PopOutChartHeader}
                    isFor3dChart={true}
                    onChartReady={setChartInstance}
                    chartInstance={chartInstance}
                    default3DOptions={default3DOptions}
                    selectedChartView={model.selectedChartView}
                    setSelectedChartView={actions.setSelectedChartView}
                    selectedXAxisUnit={model.selectedXAxisUnit}
                    setSelectedXAxisUnit={actions.setSelectedXAxisUnit}
                    setChartLoader={actions.setChartLoader}
                    popOutFooter={chartInstance && <CustomLegend
                        chartInstance={chartInstance}
                        selectedTimestamps={selectedTimestamps}
                        setSelectedTimestamps={actions.setSelectedTimestamps}
                        getFFTDataByTimestamp={actions.getFFTDataByTimestamp}
                        spectrumRangeSelection={model.selectedSpectrumRange}
                        selectedOrganization={model.selectedOrganization}
                        selectedAsset={model.selectedAsset}
                        selectedLegends={model.selectedLegends}
                        setSelectedLegends={actions.setSelectedLegends}
                        setChartLoader={actions.setChartLoader}
                    />}
                />
                {showHistoricalDialog &&
                    <FFTHistoricalTimestampDialog
                        showHistoricalDialog={showHistoricalDialog}
                        setShowHistoricalDialog={setShowHistoricalDialog}
                        setSelectedDates={actions.setSelectedDates}
                        dateSelection={model.dateSelection}
                        getFFTTimestampList={actions.getFFTTimestampList}
                        fftTimestampList={model.fftTimestampList}
                        getFFTDataByTimestamp={actions.getFFTDataByTimestamp}
                        fftDataLoadingStatus={model?.fftDataLoadingStatus}
                        selectedTimestamps={selectedTimestamps}
                        setSelectedTimestamps={actions.setSelectedTimestamps}
                        selectedOrganization={model.selectedOrganization}
                        selectedAsset={model.selectedAsset}
                        setSelectedLegends={actions.setSelectedLegends}
                        sensorType={model.sensorType}
                        trendKPIsList={model?.trendKPIsList}
                        maxPrecision={model?.fftData?.[0]?.precision}
                    />
                }
            </LoadingStatusWrapper>
        </div>
    );
};

VibrationFFTComponent.propTypes = {
    actions: PropTypes.shape({
        getDownloadRawData: PropTypes.func,
        getFFTTimestampList: PropTypes.func,
        getFFTDataByTimestamp: PropTypes.func,
        setSelectedDates: PropTypes.func,
    }),
    mainChartInst: PropTypes.object,
    model: PropTypes.shape({
        fftData: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array
        ]),
        getFFTDataLoadingStatus: PropTypes.string,
        measurementExportLoadingStatus: PropTypes.string,
        trendDataLoadingStatus: PropTypes.string,
        dateSelection: PropTypes.object,
        fftTimestampList: PropTypes.object,
        fftDataLoadingStatus: PropTypes.string,
        speed: PropTypes.any,
        sensorTypeID: PropTypes.any,
        hasRawData: PropTypes.bool,
        trendKPIsList: PropTypes.array,
        selectedSpectrumRange: PropTypes.any,
        selectedLegends: PropTypes.object,
        selectedOrganization: PropTypes.object,
        selectedAsset: PropTypes.object,
        sensorType: PropTypes.string,
        selectedChartView: PropTypes.string,
        selectedXAxisUnit: PropTypes.string
    }),
};

export default VibrationFFTComponent;
